/**
 * Post-login setup flow, shown right after a successful ONLINE sign-in:
 *  1. Forced password change (admin set must_change_password)
 *  2. First-login password creation (OTP users without a password)
 *  3. Quick PIN creation offer (4 or 6 digits, device-bound)
 * Renders as a sequence of dialogs; calls onDone() when finished/skipped.
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import { getDeviceId } from "@/lib/secureStore";
import { saveOfflinePin, saveOfflineLogin } from "@/lib/offlineAuth";
import { useT } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, LockKeyhole } from "lucide-react";

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "X-Device-Id": getDeviceId(),
});

function PasswordDialog({ open, title, desc, needsCurrent, onSubmit, onSkip, busy, t }) {
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const submit = () => {
    if (pw.length < 6) { toast.error(t("msg.passwordShort")); return; }
    if (pw !== pw2) { toast.error(t("auth.pinMismatch")); return; }
    onSubmit(pw, current);
  };
  return (
    <Dialog open={open}>
      <DialogContent className="max-w-sm" data-testid="post-login-password-dialog" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><LockKeyhole className="w-5 h-5 text-primary" /> {title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {needsCurrent && (
            <Input data-testid="setup-current-password" type="password" className="am-input" placeholder={t("auth.currentPassword")}
              value={current} onChange={(e) => setCurrent(e.target.value)} />
          )}
          <Input data-testid="setup-new-password" type="password" className="am-input" placeholder={t("auth.newPassword")}
            value={pw} onChange={(e) => setPw(e.target.value)} />
          <Input data-testid="setup-confirm-password" type="password" className="am-input" placeholder={t("auth.confirmPassword")}
            value={pw2} onChange={(e) => setPw2(e.target.value)} />
          <Button data-testid="setup-password-save" className="am-btn-primary w-full" disabled={busy} onClick={submit}>
            {busy ? "…" : t("common.save")}
          </Button>
          {onSkip && (
            <button data-testid="setup-password-skip" onClick={onSkip}
              className="w-full text-center text-sm font-semibold text-muted-foreground hover:text-foreground">
              {t("auth.skipForNow")}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PinDialog({ open, onSave, onSkip, busy, t }) {
  const [len, setLen] = useState(4);
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const digits = (v, n) => v.replace(/\D/g, "").slice(0, n);
  const submit = () => {
    if (pin.length !== len) { toast.error(t("auth.pinLengthError", { n: len })); return; }
    if (pin !== pin2) { toast.error(t("auth.pinMismatch")); return; }
    onSave(pin);
  };
  return (
    <Dialog open={open}>
      <DialogContent className="max-w-sm" data-testid="create-pin-dialog" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><KeyRound className="w-5 h-5 text-primary" /> {t("auth.createPinTitle")}</DialogTitle>
          <DialogDescription>{t("auth.createPinDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {[4, 6].map((n) => (
              <button key={n} data-testid={`pin-len-${n}`}
                onClick={() => { setLen(n); setPin(""); setPin2(""); }}
                className={`rounded-full py-2 text-sm font-bold ring-1 transition-colors ${len === n ? "bg-primary text-primary-foreground ring-primary" : "bg-white ring-border text-muted-foreground hover:text-foreground"}`}>
                {t("auth.pinDigits", { n })}
              </button>
            ))}
          </div>
          <Input data-testid="create-pin-input" type="password" inputMode="numeric" maxLength={len}
            className="am-input font-mono text-center text-2xl tracking-[0.5em]" placeholder={"•".repeat(len)}
            value={pin} onChange={(e) => setPin(digits(e.target.value, len))} />
          <Input data-testid="create-pin-confirm" type="password" inputMode="numeric" maxLength={len}
            className="am-input font-mono text-center text-2xl tracking-[0.5em]" placeholder={"•".repeat(len)}
            value={pin2} onChange={(e) => setPin2(digits(e.target.value, len))} />
          <Button data-testid="create-pin-save" className="am-btn-primary w-full" disabled={busy} onClick={submit}>
            {busy ? "…" : t("auth.createPinBtn")}
          </Button>
          <button data-testid="create-pin-skip" onClick={onSkip}
            className="w-full text-center text-sm font-semibold text-muted-foreground hover:text-foreground">
            {t("auth.skipForNow")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * props.pending = { token, user, password?, method: "password"|"otp"|"pin" }
 * onDone(updatedUser) — always called exactly once when the flow completes.
 */
export default function PostLoginSetup({ pending, onDone }) {
  const { t } = useT();
  const { token, user, password, method } = pending;
  const online = navigator.onLine;

  const deviceHasPin = !!user.pin_device_id && user.pin_device_id === getDeviceId();
  const needsForceChange = !!user.must_change_password && online;
  const needsFirstPassword = !user.has_password && online;
  const pinSkipped = sessionStorage.getItem(`am_pin_skip_${user.mobile}`) === "1";
  const offerPin = online && method !== "pin" && !deviceHasPin && !pinSkipped;

  const [stage, setStage] = useState(
    needsForceChange ? "force" : needsFirstPassword ? "first" : offerPin ? "pin" : "done"
  );
  const [busy, setBusy] = useState(false);
  // Track the password so offline credentials can be refreshed after change.
  const [livePassword, setLivePassword] = useState(password || "");

  // Finish via effect (never call onDone during render). Guarded so onDone
  // fires exactly once even if parent re-renders while stage === "done".
  const doneRef = useRef(false);
  useEffect(() => {
    if (stage === "done" && !doneRef.current) {
      doneRef.current = true;
      onDone(user);
    }
  }, [stage, onDone, user]);

  if (stage === "done") return null;

  const advanceAfterPassword = (newPw) => {
    setLivePassword(newPw);
    // refresh offline verifier with the new password
    saveOfflineLogin(user.mobile, newPw, token, user).catch(() => {});
    setStage(offerPin ? "pin" : "done");
  };

  const submitPassword = async (newPw, current) => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/change-password`,
        { new_password: newPw, current_password: current || livePassword || undefined },
        { headers: authHeaders(token) });
      toast.success(t("msg.passwordUpdated"));
      advanceAfterPassword(newPw);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("msg.resetFailed"));
    } finally { setBusy(false); }
  };

  const savePin = async (pin) => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/set-pin`, { pin }, { headers: authHeaders(token) });
      await saveOfflinePin(user.mobile, pin);
      toast.success(t("auth.pinCreated"));
      const updated = { ...user, has_pin: true, pin_device_id: getDeviceId(), pin_length: pin.length };
      onDone(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("msg.loginFailed"));
      onDone(user);
    } finally { setBusy(false); }
  };

  const skipPin = () => {
    sessionStorage.setItem(`am_pin_skip_${user.mobile}`, "1");
    onDone(user);
  };

  return (
    <>
      <PasswordDialog
        open={stage === "force"} busy={busy} t={t}
        title={t("auth.forceChangeTitle")} desc={t("auth.forceChangeDesc")}
        needsCurrent={!livePassword}
        onSubmit={submitPassword}
      />
      <PasswordDialog
        open={stage === "first"} busy={busy} t={t}
        title={t("auth.firstPasswordTitle")} desc={t("auth.firstPasswordDesc")}
        needsCurrent={false}
        onSubmit={submitPassword}
        onSkip={() => setStage(offerPin ? "pin" : "done")}
      />
      <PinDialog open={stage === "pin"} busy={busy} t={t} onSave={savePin} onSkip={skipPin} />
    </>
  );
}
